import React, { useEffect, useRef, useMemo } from 'react';
import type { ReceiptLayoutElement } from './ReceiptLayoutDesigner';
import { deduplicateOrderItems, type OrderItem } from '../../services/api';

interface ThermalReceiptProps {
  order: {
    order_number: string;
    token_number?: number;
    customer_name?: string;
    customer_phone?: string;
    order_type: string;
    table_number?: string;
    order_time: string;
    order_items: OrderItem[];
    subtotal: number;
    discount_amount: number;
    tax_amount: number;
    service_charge: number;
    total_amount: number;
    payment_method?: string;
    payment_status: string;
    paid_amount: number;
    change_amount: number;
    notes?: string;
  };
  restaurantSettings: {
    currency_symbol: string;
    currency?: string;
    zatca_phase1_enabled?: boolean;
    restaurant_name?: string;
    vat_number?: string;
  };
  receiptLayout?: ReceiptLayoutElement[];
  receiptLogoUrl?: string;
  printerWidth?: number; // Width in mm (default 80mm)
  autoPrint?: boolean; // Whether to automatically print when component mounts
  onPrintComplete?: () => void;
  zatcaQRCode?: {
    qrCodeDataUrl: string;
    qrCodeString: string;
    zatcaData: any;
  } | null;
}

const ThermalReceipt: React.FC<ThermalReceiptProps> = ({
  order,
  restaurantSettings,
  receiptLayout,
  receiptLogoUrl,
  printerWidth = 80,
  autoPrint = false,
  onPrintComplete,
  zatcaQRCode = null
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const hasPrintedRef = useRef(false);

  // Same dedupe as backend /api/print/receipt (generateEscPosReceipt): preserve additional-item lines, collapse KDS fan-out per time bucket
  const consolidatedItems = useMemo(() => {
    return deduplicateOrderItems(order.order_items || [], false, false);
  }, [order.order_items]);

  useEffect(() => {
    // Only auto-print if autoPrint is true and we haven't printed yet
    if (!autoPrint || hasPrintedRef.current || !receiptRef.current) return;

    const printReceipt = () => {
      // Double-check to prevent multiple print calls
      if (hasPrintedRef.current || !receiptRef.current) return;
      hasPrintedRef.current = true;

      // Add class to body to hide other content
      document.body.classList.add('printing-receipt');

      // Trigger print
      window.print();

      // Clean up after print
      setTimeout(() => {
        document.body.classList.remove('printing-receipt');
        if (onPrintComplete) {
          onPrintComplete();
        }
      }, 1000);
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(printReceipt, 100);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
  }, [autoPrint, onPrintComplete]);

  const formatCurrency = (amount: number | string) => {
    const symbol = restaurantSettings?.currency_symbol || 'SAR';
    const n = typeof amount === 'number' ? amount : Number(amount);
    return `${symbol} ${(Number.isNaN(n) ? 0 : n).toFixed(2)}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderElement = (element: ReceiptLayoutElement) => {
    const style: React.CSSProperties = {
      textAlign: element.align || 'left',
      fontWeight: element.bold ? 800 : 700,
      fontSize: element.fontSize === 'large' ? '18px' : element.fontSize === 'small' ? '12px' : '14px',
      marginBottom: '8px'
    };

    switch (element.type) {
      case 'logo':
        return receiptLogoUrl ? (
          <div key={element.id} style={{ textAlign: element.align || 'center', marginBottom: '12px' }}>
            <img 
              src={receiptLogoUrl} 
              alt="Receipt Logo" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '60px', 
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto'
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        ) : null;
      case 'header':
        return (
          <div key={element.id} style={{ ...style, fontSize: '18px', fontWeight: 800, marginBottom: '4px' }}>
            ORDER RECEIPT
          </div>
        );
      case 'order_number':
        return (
          <div key={element.id}>
            <div style={style}>Order #{order.order_number}</div>
            {order.order_type && (
              <div style={{ ...style, fontSize: '13px', textTransform: 'capitalize' }}>
                {order.order_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </div>
            )}
          </div>
        );
      case 'token':
        return order.token_number ? (
          <div key={element.id} style={style}>
            Token #{order.token_number}
          </div>
        ) : null;
      case 'date':
        return (
          <div key={element.id} style={{ ...style, fontSize: '12px' }}>
            {formatDateTime(order.order_time)}
          </div>
        );
      case 'divider':
        return (
          <div key={element.id} className="receipt-divider"></div>
        );
      case 'customer_info':
        return (
          <div key={element.id} className="receipt-section">
            <div className="receipt-section-title">Customer Info</div>
            <div style={{ fontSize: '13px', fontWeight: 700 }}>
              {order.customer_name || 'Guest'}
            </div>
            {order.customer_phone && (
              <div style={{ fontSize: '13px', fontWeight: 700 }}>
                {order.customer_phone}
              </div>
            )}
            <div style={{ fontSize: '13px', fontWeight: 700, textTransform: 'capitalize' }}>
              {order.order_type.replace('_', ' ')}
            </div>
            {order.table_number && (
              <div style={{ fontSize: '13px', fontWeight: 700 }}>
                Table: {order.table_number}
              </div>
            )}
          </div>
        );
      case 'items':
        return (
          <div key={element.id} className="receipt-section">
            <div className="receipt-section-title">Items</div>
            {consolidatedItems.map((item, index) => (
              <div key={index} className="receipt-item">
                <div className="receipt-item-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <div className="receipt-item-name" style={{ fontWeight: 800, fontSize: '15px', flex: 1 }}>
                    {item.item_code ? `${item.item_code} - ${item.item_name || 'Item'} - ${item.quantity}` : `${item.item_name || 'Item'} - ${item.quantity}`}
                  </div>
                  <div className="receipt-item-price" style={{ fontWeight: 700, fontSize: '14px', marginLeft: '8px' }}>
                    {formatCurrency(item.total_price)}
                  </div>
                </div>
                {item.variant_name && (
                  <div className="receipt-item-details">
                    Variant: {item.variant_name}
                    {item.variant_price_modifier > 0 && ` (+${formatCurrency(item.variant_price_modifier)})`}
                  </div>
                )}
                {item.addons && item.addons.length > 0 && (
                  <div className="receipt-item-details">
                    Addons:
                    {item.addons.map((addon, idx) => (
                      <div key={idx} style={{ marginLeft: '5px' }}>
                        - {addon.name}
                        {addon.price > 0 && ` (+${formatCurrency(addon.price)})`}
                      </div>
                    ))}
                  </div>
                )}
                {item.notes && (
                  <div className="receipt-item-details" style={{ fontStyle: 'italic' }}>
                    Note: {item.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      case 'subtotal':
        return (
          <div key={element.id} className="receipt-summary-row" style={style}>
            <span>Subtotal:</span>
            <span>{formatCurrency(order.subtotal)}</span>
          </div>
        );
      case 'discount':
        return order.discount_amount > 0 ? (
          <div key={element.id} className="receipt-summary-row" style={{ ...style, color: '#008000' }}>
            <span>Discount:</span>
            <span>-{formatCurrency(order.discount_amount)}</span>
          </div>
        ) : null;
      case 'tax':
        return (
          <div key={element.id} className="receipt-summary-row" style={style}>
            <span>Tax:</span>
            <span>{formatCurrency(order.tax_amount)}</span>
          </div>
        );
      case 'service_charge':
        return order.service_charge > 0 ? (
          <div key={element.id} className="receipt-summary-row" style={style}>
            <span>Service Charge:</span>
            <span>{formatCurrency(order.service_charge)}</span>
          </div>
        ) : null;
      case 'total':
        return (
          <div key={element.id} className="receipt-summary-row receipt-total" style={style}>
            <span>Total:</span>
            <span>{formatCurrency(order.total_amount)}</span>
          </div>
        );
      case 'payment_info':
        return order.payment_method ? (
          <div key={element.id} className="receipt-section">
            <div className="receipt-summary-row" style={style}>
              <span>Payment Method:</span>
              <span style={{ textTransform: 'capitalize' }}>{order.payment_method.replace('_', ' ')}</span>
            </div>
            {order.paid_amount > 0 && (
              <div className="receipt-summary-row" style={style}>
                <span>Paid:</span>
                <span>{formatCurrency(order.paid_amount)}</span>
              </div>
            )}
            {order.change_amount > 0 && (
              <div className="receipt-summary-row" style={style}>
                <span>Change:</span>
                <span>{formatCurrency(order.change_amount)}</span>
              </div>
            )}
            <div className="receipt-summary-row" style={style}>
              <span>Status:</span>
              <span style={{ textTransform: 'capitalize', fontWeight: 800 }}>{order.payment_status}</span>
            </div>
          </div>
        ) : null;
      case 'notes':
        return order.notes ? (
          <div key={element.id} className="receipt-section">
            <div style={{ fontSize: '13px', fontWeight: 700 }}>
              <strong>Notes:</strong> {order.notes}
            </div>
          </div>
        ) : null;
      case 'footer':
        return (
          <div key={element.id} className="receipt-footer" style={style}>
            <div style={{ fontWeight: 800 }}>Thank you for your order!</div>
          </div>
        );
      case 'custom_text':
        return (
          <div key={element.id} style={style}>
            {element.customText || ''}
          </div>
        );
      default:
        return null;
    }
  };

  // Use custom layout if provided, otherwise use default layout
  // Ensure layout is an array and properly formatted
  let layoutToUse: ReceiptLayoutElement[] | null = null;
  if (receiptLayout) {
    // Handle case where layout might be a string (JSON) or already parsed
    let parsedLayout: any = receiptLayout;
    if (typeof receiptLayout === 'string') {
      try {
        parsedLayout = JSON.parse(receiptLayout);
      } catch (e) {
        console.error('Error parsing receipt layout:', e);
        parsedLayout = null;
      }
    }
    
    if (Array.isArray(parsedLayout) && parsedLayout.length > 0) {
      layoutToUse = parsedLayout
        .filter((el: any) => el && el.enabled !== false)
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
    }
  }
  
  // Warn if layout is provided but not being used
  if (receiptLayout && (!layoutToUse || layoutToUse.length === 0)) {
    console.warn('Receipt layout provided but not usable. Layout:', receiptLayout);
  }

  const printerWidthMm = printerWidth || 80;

  return (
    <>
      <style>{`
        @media print {
          body.printing-receipt * {
            visibility: hidden;
          }
          body.printing-receipt .receipt-container,
          body.printing-receipt .receipt-container * {
            visibility: visible;
          }
          body.printing-receipt .receipt-container {
            position: fixed;
            left: 0;
            top: 0;
            width: ${printerWidthMm}mm;
            max-width: ${printerWidthMm}mm;
            margin: 0;
            padding: 0;
          }
          @page {
            size: ${printerWidthMm}mm auto;
            margin: 0;
          }
        }
        .receipt-wrapper {
          display: none;
        }
        @media print {
          .receipt-wrapper {
            display: block;
          }
        }
        .receipt-container {
          width: ${printerWidthMm}mm;
          max-width: ${printerWidthMm}mm;
          margin: 0 auto;
          padding: 10mm 5mm;
          font-family: 'Calibri', 'Helvetica Neue', Helvetica, Arial, sans-serif;
          background: white;
          color: #000;
          font-size: 14px;
          line-height: 1.4;
        }
        .receipt-header {
          text-align: center;
          margin-bottom: 8px;
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
        }
        .receipt-title {
          font-size: 18px;
          font-weight: 800;
          margin-bottom: 4px;
        }
        .receipt-order-info {
          font-size: 13px;
          font-weight: 700;
          margin: 4px 0;
        }
        .receipt-section {
          margin: 8px 0;
        }
        .receipt-section-title {
          font-size: 15px;
          font-weight: 800;
          margin-bottom: 4px;
          border-bottom: 1px solid #000;
          padding-bottom: 2px;
        }
        .receipt-item {
          margin: 6px 0;
          padding-bottom: 4px;
          border-bottom: 1px dotted #ccc;
        }
        .receipt-item-name {
          font-size: 15px;
          font-weight: 800;
          margin-bottom: 2px;
        }
        .receipt-item-details {
          font-size: 13px;
          color: #000;
          margin-left: 10px;
          margin-top: 3px;
          font-weight: 400;
          letter-spacing: 0.3px;
        }
        .receipt-item-price {
          font-size: 14px;
          font-weight: 700;
          text-align: right;
          margin-top: 2px;
        }
        .receipt-divider {
          border-top: 1px dashed #000;
          margin: 8px 0;
          width: 100%;
          max-width: 100%;
        }
        .receipt-summary-row {
          display: flex;
          justify-content: space-between;
          margin: 4px 0;
          font-size: 14px;
          font-weight: 700;
        }
        .receipt-total {
          font-size: 16px;
          font-weight: 800;
          border-top: 2px solid #000;
          padding-top: 4px;
          margin-top: 4px;
        }
        .receipt-footer {
          text-align: center;
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px dashed #000;
          font-size: 12px;
          font-weight: 700;
        }
        .text-center {
          text-align: center;
        }
        .text-right {
          text-align: right;
        }
      `}</style>
      <div className="receipt-wrapper">
        <div ref={receiptRef} className="receipt-container">
          {layoutToUse && layoutToUse.length > 0 ? (
            // Render using custom layout
            <>
              {layoutToUse.map(element => {
                if (!element || !element.type) {
                  console.warn('Invalid layout element:', element);
                  return null;
                }
                return renderElement(element);
              }).filter(Boolean)}
            </>
          ) : (
            // Default layout (fallback)
            <>
              <div className="receipt-header">
                <div className="receipt-title">ORDER RECEIPT</div>
                <div className="receipt-order-info">
                  Order #{order.order_number}
                </div>
                {order.token_number && (
                  <div className="receipt-order-info">
                    Token #{order.token_number}
                  </div>
                )}
                {order.order_type && (
                  <div className="receipt-order-info" style={{ textTransform: 'capitalize' }}>
                    {order.order_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </div>
                )}
                <div className="receipt-order-info" style={{ fontSize: '12px', fontWeight: 700 }}>
                  {formatDateTime(order.order_time)}
                </div>
              </div>

              <div className="receipt-section">
                <div className="receipt-section-title">Customer Info</div>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>
                  {order.customer_name || 'Guest'}
                </div>
                {order.customer_phone && (
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>
                    {order.customer_phone}
                  </div>
                )}
                <div style={{ fontSize: '13px', fontWeight: 700, textTransform: 'capitalize' }}>
                  {order.order_type.replace('_', ' ')}
                </div>
                {order.table_number && (
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>
                    Table: {order.table_number}
                  </div>
                )}
              </div>

              <div className="receipt-divider"></div>

              <div className="receipt-section">
                <div className="receipt-section-title">Items</div>
                {consolidatedItems.map((item, index) => (
                  <div key={index} className="receipt-item">
                    <div className="receipt-item-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <div className="receipt-item-name" style={{ fontWeight: 800, fontSize: '15px', flex: 1 }}>
                        {item.item_code ? `${item.item_code} - ${item.item_name || 'Item'} - ${item.quantity}` : `${item.item_name || 'Item'} - ${item.quantity}`}
                      </div>
                      <div className="receipt-item-price" style={{ fontWeight: 700, fontSize: '14px', marginLeft: '8px' }}>
                        {formatCurrency(item.total_price)}
                      </div>
                    </div>
                    {item.variant_name && (
                      <div className="receipt-item-details">
                        Variant: {item.variant_name}
                        {item.variant_price_modifier > 0 && ` (+${formatCurrency(item.variant_price_modifier)})`}
                      </div>
                    )}
                    {item.addons && item.addons.length > 0 && (
                      <div className="receipt-item-details">
                        Addons:
                        {item.addons.map((addon, idx) => (
                          <div key={idx} style={{ marginLeft: '5px' }}>
                            - {addon.name}
                            {addon.price > 0 && ` (+${formatCurrency(addon.price)})`}
                          </div>
                        ))}
                      </div>
                    )}
                    {item.notes && (
                      <div className="receipt-item-details" style={{ fontStyle: 'italic' }}>
                        Note: {item.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="receipt-divider"></div>

              <div className="receipt-section">
                <div className="receipt-summary-row">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(order.subtotal)}</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="receipt-summary-row" style={{ color: '#008000' }}>
                    <span>Discount:</span>
                    <span>-{formatCurrency(order.discount_amount)}</span>
                  </div>
                )}
                <div className="receipt-summary-row">
                  <span>Tax:</span>
                  <span>{formatCurrency(order.tax_amount)}</span>
                </div>
                {order.service_charge > 0 && (
                  <div className="receipt-summary-row">
                    <span>Service Charge:</span>
                    <span>{formatCurrency(order.service_charge)}</span>
                  </div>
                )}
                <div className="receipt-summary-row receipt-total">
                  <span>Total:</span>
                  <span>{formatCurrency(order.total_amount)}</span>
                </div>
              </div>

              {order.payment_method && (
                <>
                  <div className="receipt-divider"></div>
                  <div className="receipt-section">
                    <div className="receipt-summary-row">
                      <span>Payment Method:</span>
                      <span style={{ textTransform: 'capitalize' }}>{order.payment_method.replace('_', ' ')}</span>
                    </div>
                    {order.paid_amount > 0 && (
                      <div className="receipt-summary-row">
                        <span>Paid:</span>
                        <span>{formatCurrency(order.paid_amount)}</span>
                      </div>
                    )}
                    {order.change_amount > 0 && (
                      <div className="receipt-summary-row">
                        <span>Change:</span>
                        <span>{formatCurrency(order.change_amount)}</span>
                      </div>
                    )}
                    <div className="receipt-summary-row">
                      <span>Status:</span>
                      <span style={{ textTransform: 'capitalize', fontWeight: 800 }}>{order.payment_status}</span>
                    </div>
                  </div>
                </>
              )}

              {order.notes && (
                <>
                  <div className="receipt-divider"></div>
                  <div className="receipt-section">
                    <div style={{ fontSize: '13px', fontWeight: 700 }}>
                      <strong>Notes:</strong> {order.notes}
                    </div>
                  </div>
                </>
              )}

              {restaurantSettings?.zatca_phase1_enabled && zatcaQRCode && (
                <>
                  <div className="receipt-divider"></div>
                  <div className="receipt-section" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '8px' }}>
                      ZATCA Tax Invoice
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <img 
                        src={zatcaQRCode.qrCodeDataUrl} 
                        alt="ZATCA QR Code" 
                        style={{ 
                          width: '250px', 
                          height: '250px',
                          maxWidth: '250px', 
                          maxHeight: '250px',
                          display: 'block',
                          margin: '0 auto'
                        }}
                      />
                    </div>
                    {restaurantSettings.vat_number && (
                      <div style={{ fontSize: '11px', fontWeight: 700, marginTop: '4px' }}>
                        VAT: {restaurantSettings.vat_number}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="receipt-footer">
                <div style={{ fontWeight: 800 }}>Thank you for your order!</div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ThermalReceipt;

